import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  FiUsers, FiPlus, FiCheck, FiX, FiUserMinus, FiMapPin,
  FiMail, FiLogOut, FiUserPlus, FiInbox,
} from 'react-icons/fi';
import {
  getMyFamily, createFamily, joinFamilyByEmail,
  approveFamilyMember, removeFamilyMember, leaveFamily,
  inviteFamilyMember, respondToFamilyInvite,
} from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import styles from './Family.module.css';

const AddressBlock = ({ family }) => {
  const parts = [
    family.flatNo ? `${family.flatNo}, ${family.buildingName || ''}`.trim() : family.buildingName,
    family.addressLine1, family.addressLine2,
    family.city, family.state, family.pincode, family.country,
  ].filter(Boolean);
  if (!parts.length) return <span className={styles.muted}>No address provided</span>;
  return <span>{parts.join(', ')}</span>;
};

// memberUserId holds User.id for all rows — use this for identity checks & actions
const memberUid = (m) => String(m.memberUserId ?? m.userId ?? '');

const UserFamilyPage = () => {
  const { user, activeDerasarId } = useAuth();

  const [family, setFamily]           = useState(null);
  const [loading, setLoading]         = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  const [addMemberModal, setAddMemberModal] = useState(false);
  const [inviteEmail, setInviteEmail]       = useState('');
  const [inviting, setInviting]             = useState(false);

  const [joinModal, setJoinModal]           = useState(false);
  const [joinHeadEmail, setJoinHeadEmail]   = useState('');
  const [joiningByEmail, setJoiningByEmail] = useState(false);

  const [createModal, setCreateModal] = useState(false);
  const [createForm, setCreateForm]   = useState({
    name: '', flatNo: '', buildingName: '', addressLine1: '', addressLine2: '',
    city: '', state: '', pincode: '', country: 'India',
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!activeDerasarId) return;
    setLoading(true);
    try {
      const res = await getMyFamily(activeDerasarId);
      setFamily(res || null);
    } catch {
      toast.error('Failed to load family');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [activeDerasarId]);

  // -- Derived state ------------------------------------------------------------
  const myMembership  = family?.members?.find((m) => String(m.memberUserId) === String(user?.id));
  const inFamily      = myMembership?.status === 'approved';
  const hasPending    = myMembership?.status === 'pending';
  const imHead        = family ? String(family.headId) === String(user?.id) : false;
  const approvedMembers = family?.members?.filter((m) => m.status === 'approved') || [];
  const pendingMembers  = family?.members?.filter((m) => m.status === 'pending')  || [];
  const invitedMembers  = family?.members?.filter((m) => m.status === 'invited')  || [];

  const myInvitation = (() => {
    if (family) {
      const inv = family.members?.find(
        (m) => String(m.memberUserId) === String(user?.id) && m.status === 'invited'
      );
      if (inv) return family;
    }
    return null;
  })();

  // -- Handlers -----------------------------------------------------------------
  const handleCreate = async () => {
    if (!createForm.name.trim()) { toast.error('Family name is required'); return; }
    setSaving(true);
    try {
      await createFamily({ ...createForm, derasarId: activeDerasarId });
      toast.success('Family created! You are now the head.');
      setCreateModal(false);
      setCreateForm({ name: '', flatNo: '', buildingName: '', addressLine1: '', addressLine2: '', city: '', state: '', pincode: '', country: 'India' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create family');
    } finally {
      setSaving(false);
    }
  };

  const handleJoinByEmail = async () => {
    if (!joinHeadEmail.trim()) { toast.error("Please enter the head's email address"); return; }
    setJoiningByEmail(true);
    try {
      await joinFamilyByEmail(joinHeadEmail.trim(), activeDerasarId);
      toast.success('Join request sent to family head');
      setJoinModal(false);
      setJoinHeadEmail('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send request');
    } finally {
      setJoiningByEmail(false);
    }
  };

  const handleApprove = async (memberUserId, action) => {
    setActionLoading(`${memberUserId}-${action}`);
    try {
      await approveFamilyMember(family.id, memberUserId, action);
      toast.success(`Member ${action}d`);
      load();
    } catch {
      toast.error('Failed to update');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemove = async (memberUserId) => {
    if (!window.confirm('Remove this member from the family?')) return;
    setActionLoading(`remove-${memberUserId}`);
    try {
      await removeFamilyMember(family.id, memberUserId);
      toast.success('Member removed');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to remove member');
    } finally {
      setActionLoading(null);
    }
  };

  const handleLeave = async () => {
    if (!family) return;
    const msg = imHead
      ? 'Leave? Headship transfers to next member or family is deleted if you are the only one.'
      : 'Leave this family?';
    if (!window.confirm(msg)) return;
    setActionLoading('leave');
    try {
      await leaveFamily(family.id);
      toast.success('You have left the family');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to leave family');
    } finally {
      setActionLoading(null);
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) { toast.error('Please enter an email address'); return; }
    setInviting(true);
    try {
      await inviteFamilyMember(family.id, inviteEmail.trim());
      toast.success('Invitation sent!');
      setAddMemberModal(false);
      setInviteEmail('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleInviteRespond = async (action) => {
    setActionLoading(`invite-${action}`);
    try {
      await respondToFamilyInvite(family.id, action);
      toast.success(action === 'accept' ? 'You have joined the family!' : 'Invitation declined');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to respond');
    } finally {
      setActionLoading(null);
    }
  };

  if (!activeDerasarId) return <div className="empty-state"><p>Select a derasar to manage family</p></div>;
  if (loading) return <div className="loading"><div className="spinner" /></div>;

  // -- Invited ------------------------------------------------------------------
  if (myInvitation && !inFamily && !hasPending) {
    return (
      <div>
        <div className="page-header"><h2>Family</h2></div>
        <div className={styles.inviteCard}>
          <div className={styles.inviteCardIcon}><FiInbox size={20} color="#fff" /></div>
          <div className={styles.inviteCardBody}>
            <div className={styles.inviteCardTitle}>Family Invitation</div>
            <div className={styles.inviteCardMeta}>
              <strong>{family.name}</strong>
              {family.head?.name && ` · Head: ${family.head.name}`}
            </div>
          </div>
          <div className={styles.inviteCardActions}>
            <button className="btn btn-success btn-sm" onClick={() => handleInviteRespond('accept')} disabled={actionLoading === 'invite-accept'}>
              <FiCheck size={13} /> Accept
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => handleInviteRespond('decline')} disabled={actionLoading === 'invite-decline'}>
              <FiX size={13} /> Decline
            </button>
          </div>
        </div>
      </div>
    );
  }

  // -- Pending -------------------------------------------------------------------
  if (hasPending && family) {
    return (
      <div>
        <div className="page-header"><h2>Family</h2></div>
        <div className={styles.pendingState}>
          <FiUsers size={32} className={styles.pendingIcon} />
          <h3>Request Pending</h3>
          <p>Your request to join <strong>{family.name}</strong> is waiting for the head's approval.</p>
          <button className="btn btn-ghost btn-sm" onClick={handleLeave} disabled={actionLoading === 'leave'}>
            <FiX size={13} /> {actionLoading === 'leave' ? 'Cancelling…' : 'Cancel Request'}
          </button>
        </div>
      </div>
    );
  }

  // -- In a family ---------------------------------------------------------------
  if (inFamily && family) {
    return (
      <>
        <div className="page-header">
          <h2>My Family</h2>
          <div style={{ display: 'flex', gap: 8 }}>
            {imHead && (
              <button className="btn btn-primary btn-sm" onClick={() => setAddMemberModal(true)}>
                <FiUserPlus size={13} /> Add Member
              </button>
            )}
            <button className="btn btn-ghost btn-sm" onClick={handleLeave} disabled={actionLoading === 'leave'}>
              <FiLogOut size={13} /> {actionLoading === 'leave' ? 'Leaving…' : 'Leave Family'}
            </button>
          </div>
        </div>

        <div className={`card ${styles.familyInfoCard}`}>
          <div className={styles.familyInfoHeader}>
            <div>
              <div className={styles.familyTitle}>{family.name}</div>
              <div className={styles.familyMeta}>
                <FiUsers size={12} /> {approvedMembers.length} member{approvedMembers.length !== 1 ? 's' : ''}
                {imHead && <span className={styles.headBadge}>You are the head</span>}
              </div>
            </div>
          </div>
          <div className={styles.familyInfoBody}>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}><FiMapPin size={11} /> Address</span>
              <span className={styles.infoValue}><AddressBlock family={family} /></span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}><FiUsers size={11} /> Head</span>
              <span className={styles.infoValue}>
                {family.head?.name || '—'}{imHead ? ' (You)' : ''}
                {family.head?.email && <span className={styles.muted}> · {family.head.email}</span>}
              </span>
            </div>
          </div>
        </div>

        {/* Pending requests (head only) */}
        {imHead && pendingMembers.length > 0 && (
          <div className="card" style={{ marginTop: 16 }}>
            <div className="card-header"><h3>Pending Requests ({pendingMembers.length})</h3></div>
            <div className="table-container">
              <table>
                <thead><tr><th>Name</th><th>Phone</th><th>Email</th><th>Actions</th></tr></thead>
                <tbody>
                  {pendingMembers.map((m) => {
                    const mUid = memberUid(m);
                    const display = m.memberUser || {};
                    return (
                      <tr key={mUid}>
                        <td>{display.name || '—'}</td>
                        <td>{display.phone || '—'}</td>
                        <td>{display.email || '—'}</td>
                        <td>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button className="btn btn-success btn-sm" disabled={!!actionLoading} onClick={() => handleApprove(mUid, 'approve')}>
                              <FiCheck size={12} /> Approve
                            </button>
                            <button className="btn btn-danger btn-sm" disabled={!!actionLoading} onClick={() => handleApprove(mUid, 'reject')}>
                              <FiX size={12} /> Reject
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Approved members */}
        <div className="card" style={{ marginTop: 16 }}>
          <div className="card-header"><h3>Members ({approvedMembers.length})</h3></div>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Person</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>Role</th>
                  {imHead && <th />}
                </tr>
              </thead>
              <tbody>
                {approvedMembers.map((m) => {
                  const mUid = memberUid(m);
                  const isThisHead = String(family.headId) === mUid;
                  const person = m.member;
                  return (
                    <tr key={`${mUid}-${m.userId}`}>
                      <td>{person?.name || '—'}</td>
                      <td>{person?.phone || '—'}</td>
                      <td>{person?.email || '—'}</td>
                      <td>
                        <span className={`badge ${isThisHead ? 'badge-admin' : 'badge-approved'}`}>
                          {isThisHead ? 'Head' : 'Member'}
                        </span>
                      </td>
                      {imHead && (
                        <td>
                          <button className="btn btn-ghost btn-sm" disabled={actionLoading === `remove-${mUid}`} onClick={() => handleRemove(mUid)}>
                            <FiUserMinus size={12} />
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pending invites (head only) */}
        {imHead && invitedMembers.length > 0 && (
          <div className="card" style={{ marginTop: 16 }}>
            <div className="card-header"><h3>Pending Invites ({invitedMembers.length})</h3></div>
            <div className="table-container">
              <table>
                <thead><tr><th>Name</th><th>Email</th><th /></tr></thead>
                <tbody>
                  {invitedMembers.map((m) => {
                    const mUid = memberUid(m);
                    const display = m.memberUser || {};
                    return (
                      <tr key={mUid}>
                        <td>{display.name || '—'}</td>
                        <td>{display.email || '—'}</td>
                        <td>
                          <button className="btn btn-ghost btn-sm" onClick={() => handleRemove(mUid)} disabled={actionLoading === `remove-${mUid}`} title="Cancel invite">
                            <FiX size={12} /> Cancel
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Add Member Modal */}
        {addMemberModal && (
          <div className="modal-overlay" onClick={() => setAddMemberModal(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <h3 style={{ margin: 0 }}>Invite Member</h3>
                <button className="btn btn-ghost btn-sm" onClick={() => setAddMemberModal(false)}><FiX size={16} /></button>
              </div>
              <div className="form-group">
                <label>Member's email address</label>
                <input type="email" autoFocus placeholder="member@example.com" value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleInvite()} />
              </div>
              <div className="modal-actions">
                <button className="btn btn-ghost" onClick={() => { setAddMemberModal(false); setInviteEmail(''); }}>Cancel</button>
                <button className="btn btn-primary" onClick={handleInvite} disabled={inviting}>
                  {inviting ? 'Sending…' : <><FiUserPlus size={13} /> Send Invite</>}
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // ── No family — Create or Join ────────────────────────────────────────────────
  return (
    <div>
      <div className="page-header"><h2>Family</h2></div>

      <div className={styles.actionCards}>
        <div className={`${styles.actionCard} ${styles.actionCardCreate}`}>
          <div className={styles.actionCardIcon}><FiPlus size={26} color="#fff" /></div>
          <h3 className={styles.actionCardTitle}>Create Your Family</h3>
          <p className={styles.actionCardDesc}>Start a new family group. You become the head.</p>
          <span className={styles.actionCardTag}>You become the Head</span>
          <button className={styles.actionCardBtn} onClick={() => setCreateModal(true)}>
            <FiPlus size={15} /> Create Family
          </button>
        </div>

        <div className={`${styles.actionCard} ${styles.actionCardJoin}`}>
          <div className={styles.actionCardIcon}><FiUsers size={26} color="#fff" /></div>
          <h3 className={styles.actionCardTitle}>Join a Family</h3>
          <p className={styles.actionCardDesc}>Enter the family head's email to send a join request.</p>
          <span className={styles.actionCardTag}>Request via head's email</span>
          <button className={styles.actionCardBtn} onClick={() => setJoinModal(true)}>
            <FiMail size={15} /> Join Family
          </button>
        </div>
      </div>

      {/* Create Family Modal */}
      {createModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 520 }}>
            <h3>Create Your Family</h3>
            <div className="form-group">
              <label>Family Name *</label>
              <input value={createForm.name} onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g., Shah Family" autoFocus />
            </div>
            <div className={styles.addrGrid}>
              {[['flatNo','Flat / Bunglow No.','e.g. A-101'],['buildingName','Building Name','e.g. Shanti Niketan'],
                ['addressLine1','Address Line 1','Street / Area'],['addressLine2','Address Line 2','Landmark (optional)'],
                ['city','City','City'],['state','State','State'],['pincode','Pincode','Pincode'],['country','Country','Country']
              ].map(([key, label, ph], i) => (
                <div className="form-group" key={key} style={i === 2 || i === 3 ? { gridColumn: '1 / -1' } : {}}>
                  <label>{label}</label>
                  <input value={createForm[key]} onChange={(e) => setCreateForm((f) => ({ ...f, [key]: e.target.value }))} placeholder={ph} />
                </div>
              ))}
            </div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setCreateModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleCreate} disabled={saving}>
                {saving ? 'Creating…' : 'Create Family'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Join Family Modal */}
      {joinModal && (
        <div className="modal-overlay" onClick={() => setJoinModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h3 style={{ margin: 0 }}>Join a Family</h3>
              <button className="btn btn-ghost btn-sm" onClick={() => setJoinModal(false)}><FiX size={16} /></button>
            </div>
            <div className="form-group">
              <label>Head's email address</label>
              <input type="email" autoFocus placeholder="familyhead@example.com" value={joinHeadEmail}
                onChange={(e) => setJoinHeadEmail(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleJoinByEmail()} />
            </div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => { setJoinModal(false); setJoinHeadEmail(''); }}>Cancel</button>
              <button className="btn btn-primary" onClick={handleJoinByEmail} disabled={joiningByEmail}>
                {joiningByEmail ? 'Sending…' : <><FiUsers size={13} /> Send Request</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserFamilyPage;
